import { FormEvent, useCallback, useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { useAuthContext } from '@/lib/auth-context'
import { coreApi } from '@/lib/coreApi'

interface PoolNominee {
  id: string
  type: 'teacher' | 'prospect'
  name: string
  detail: string | null
  weight: number
  support_code: string | null
  status: string
}

interface PoolData {
  my_prospects: PoolNominee[]
  upcoming_ceremony: { id: string; kind: string; slots_available: number; scheduled_at: string } | null
}

export default function LockerRoomNominees() {
  const { profile } = useAuthContext() as any
  const [data, setData] = useState<PoolData | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')

  // Nominate form
  const [showForm, setShowForm] = useState(false)
  const [name, setName] = useState('')
  const [email, setEmail] = useState('')
  const [sport, setSport] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [message, setMessage] = useState('')

  const load = useCallback(async () => {
    setLoading(true)
    try {
      const res = await coreApi<{ data: PoolData }>('/api/v1/nominations/pool')
      setData(res.data)
    } catch (err: any) {
      setError(err?.message || 'Failed to load')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => { load() }, [load])

  const handleNominate = useCallback(async (e: FormEvent) => {
    e.preventDefault()
    if (!name.trim()) return
    setSubmitting(true)
    setMessage('')
    try {
      await coreApi('/api/v1/nominations/pool/add-prospect', {
        method: 'POST',
        body: { prospect_name: name.trim(), prospect_email: email.trim() || undefined, sport: sport.trim() || undefined },
      })
      setMessage('Nominated! Confirmation email sent.')
      setName(''); setEmail(''); setSport('')
      setShowForm(false)
      await load()
    } catch (err: any) {
      setMessage(err?.message || 'Failed')
    } finally {
      setSubmitting(false)
    }
  }, [name, email, sport, load])

  const formatDate = (d: string) => {
    try { return new Date(d).toLocaleDateString(undefined, { month: 'long', day: 'numeric', year: 'numeric' }) } catch { return d }
  }

  return (
    <section className="space-y-6">
      <div className="flex items-start justify-between gap-3">
        <div>
          <h2 className="text-lg font-semibold text-stone-900">My Nominees</h2>
          <p className="text-sm text-stone-600">Prospects you've nominated for the draw pool. Share support links to increase weight before each ceremony.</p>
        </div>
        <button
          type="button"
          onClick={() => setShowForm(!showForm)}
          className="shrink-0 rounded-xl bg-stone-900 px-4 py-2 text-xs font-semibold text-white transition hover:bg-stone-800"
        >
          {showForm ? 'Cancel' : '+ Nominate Prospect'}
        </button>
      </div>

      {error && <p className="text-sm text-brick">{error}</p>}

      {/* Nominate form */}
      {showForm && (
        <form onSubmit={handleNominate} className="rounded-xl border border-dashed border-stone-300 bg-stone-50 p-4 space-y-3">
          <p className="text-xs font-semibold text-stone-700">New nomination — enters the draw pool on confirmation</p>
          <div className="grid gap-3 sm:grid-cols-3">
            <input className="rounded-xl border border-stone-200 px-3 py-2 text-sm focus:border-stone-400 focus:outline-none" value={name} onChange={(e) => setName(e.target.value)} placeholder="Prospect name *" required />
            <input type="email" className="rounded-xl border border-stone-200 px-3 py-2 text-sm focus:border-stone-400 focus:outline-none" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Prospect email" />
            <input className="rounded-xl border border-stone-200 px-3 py-2 text-sm focus:border-stone-400 focus:outline-none" value={sport} onChange={(e) => setSport(e.target.value)} placeholder="Sport" />
          </div>
          <div className="flex items-center gap-3">
            <button type="submit" disabled={submitting || !name.trim()} className="rounded-xl bg-stone-900 px-4 py-2 text-xs font-semibold text-white disabled:opacity-50">
              {submitting ? 'Submitting...' : 'Nominate & Send Email'}
            </button>
            {message && <span className="text-xs text-stone-600">{message}</span>}
          </div>
        </form>
      )}

      {/* Upcoming ceremony */}
      {data?.upcoming_ceremony && (
        <div className="rounded-xl bg-gradient-to-r from-stone-900 to-stone-800 p-4 text-white">
          <p className="text-[10px] font-bold uppercase tracking-widest text-white/60">Next Ceremony</p>
          <p className="text-sm font-semibold">
            {data.upcoming_ceremony.kind === 'classroom' ? 'Classroom' : 'Locker Room'} Draw — {formatDate(data.upcoming_ceremony.scheduled_at)}
          </p>
          <p className="text-xs text-white/70">{data.upcoming_ceremony.slots_available} slot{data.upcoming_ceremony.slots_available !== 1 ? 's' : ''} available</p>
        </div>
      )}

      {loading ? (
        <div className="py-8 text-center text-sm text-stone-500">Loading...</div>
      ) : (data?.my_prospects || []).length > 0 ? (
        <div className="space-y-2">
          {data!.my_prospects.map((p) => (
            <div key={p.id} className="flex items-center justify-between rounded-xl border border-stone-200 bg-white p-4 shadow-sm">
              <div>
                <p className="text-sm font-semibold text-stone-900">{p.name}</p>
                <div className="mt-0.5 flex items-center gap-2 text-xs text-stone-500">
                  {p.detail && <span>{p.detail}</span>}
                  <span className={`rounded-full px-2 py-0.5 text-[10px] font-bold uppercase ${
                    p.status === 'pool' ? 'bg-emerald-100 text-emerald-700' :
                    p.status === 'confirmed' ? 'bg-blue-100 text-blue-700' :
                    p.status === 'drawn' ? 'bg-purple-100 text-purple-700' :
                    'bg-stone-100 text-stone-600'
                  }`}>
                    {p.status}
                  </span>
                </div>
              </div>
              <div className="flex items-center gap-4">
                <div className="text-right">
                  <p className="text-2xl font-bold text-stone-900">{p.weight}</p>
                  <p className="text-[10px] text-stone-400">weight</p>
                </div>
                {p.support_code && (
                  <div className="flex flex-col gap-1">
                    <button
                      type="button"
                      className="rounded-lg bg-stone-900 px-3 py-1 text-[10px] font-semibold text-white"
                      onClick={() => navigator.clipboard.writeText(`${window.location.origin}/support/${p.support_code}`)}
                    >
                      Copy Link
                    </button>
                    <Link to={`/support/${p.support_code}`} target="_blank" className="text-center text-[10px] text-stone-400 underline">Preview</Link>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="rounded-xl border border-dashed border-stone-300 py-8 text-center text-sm text-stone-500">
          No nominees yet. Use the button above to nominate a prospect for the draw pool.
        </div>
      )}

      <div className="text-center">
        <Link to="/portal/nominee" className="text-xs text-stone-500 underline hover:text-stone-700">View the full pool →</Link>
      </div>
    </section>
  )
}
